import { RefObject, useImperativeHandle, useState } from "react";

import {
	Button,
	Dialog,
	DialogContent,
	IconButton,
	Typography,
} from "@mui/material";
import classNames from "classnames";
import { useForm, useWatch } from "react-hook-form";
import { FaXmark } from "react-icons/fa6";
import { toast } from "react-toastify";

import { apis, callApi } from "../../../../../api";
import { validators } from "../../../../../utils";
import { CommonInput } from "../../../../common";

import "./request_money_dialog.scss";

export type TRequestMoneyFieldValues = {
	message: string;
	totalCost: string;
};

export type TRequestMoneyDialogProps = {
	ref: RefObject<TRequestMoneyDialogRef | null>;
};

export type TRequestMoneyDialogRef = {
	open: (studentName: string, studentId: number, summaryId: number) => void;
};

export function RequestMoneyDialog(props: TRequestMoneyDialogProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [studentInfo, setStudentInfo] = useState<{
		studentName: string;
		studentId: number;
		summaryId: number;
	} | null>(null);
	const { control, handleSubmit, reset } = useForm<TRequestMoneyFieldValues>({
		defaultValues: {
			message: "",
			totalCost: "",
		},
		mode: "onChange",
	});

	const { message, totalCost } = useWatch({ control });

	const open = (studentName: string, studentId: number, summaryId: number) => {
		setIsOpen(true);
		setStudentInfo({ studentName, studentId, summaryId });
	};

	const close = () => {
		reset();
		setIsOpen(false);
	};

	useImperativeHandle(props.ref, () => ({ open }), []);

	const onClickRequest = async (data: TRequestMoneyFieldValues) => {
		try {
			if (!studentInfo) {
				return;
			}
			const response = await callApi({
				method: "POST",
				url: apis.BILL,
				body: {
					summaryId: studentInfo.summaryId,
					studentId: studentInfo.studentId,
					totalCost: Number(data.totalCost) * 100,
					message: data.message,
				},
			});

			if (!response) {
				return;
			}

			toast.success(
				`Payment request has been sent to ${studentInfo.studentName}!`,
			);
		} catch (error) {
		} finally {
			close();
		}
	};

	const isDisabled = message?.trim() === "" || Number(totalCost) === 0;

	return (
		<Dialog open={isOpen} onClose={close} className="request-money-dialog">
			<DialogContent>
				<div className="dialog-header">
					<IconButton onClick={close}>
						<FaXmark className="normal-icon close-icon" />
					</IconButton>
				</div>

				<form className="form" onSubmit={handleSubmit(onClickRequest)}>
					<Typography className="form-title">Request money</Typography>
					<div>
						<Typography className="big-text bold">
							{`Note for ${studentInfo?.studentName}`}
						</Typography>
						<Typography className="normal-text">
							What is this request for? Include anything that wasn't included in
							the cost of the course (ex: additional training, airplane ferry,
							travel, lodging, or meals not included on course description)
						</Typography>
						<CommonInput
							control={control}
							name="message"
							placeholder="Enter reason here..."
							multiline
							rules={{
								required: {
									value: true,
									message: "Reason is required.",
								},
							}}
						/>
					</div>
					<div>
						<Typography className="big-text bold">Total cost</Typography>
						<CommonInput
							control={control}
							name="totalCost"
							prefix="$"
							disallowInvalidInput
							rules={{
								required: {
									value: true,
									message: "Total cost is required.",
								},
								pattern: {
									value: validators.price,
									message: "Price is invalid.",
								},
							}}
						/>
					</div>
					<Button
						className={classNames("normal-button submit-button", {
							"primary-button": !isDisabled,
							"disabled-button": isDisabled,
						})}
						disabled={isDisabled}
						type="submit">
						Request
					</Button>
				</form>
			</DialogContent>
		</Dialog>
	);
}
